<script>
import moment from "moment";

const BASE_FILTERS = {
  proofId: 0,
  senderId: 0,
  workflowId: 0,
  approverId: 0,
  overdue: false,
  sortByDeadlineDesc: false,
  tagId: 0,
  text: "",
};

export default {
  name: "recent-proof-lists",
  props: {
    associatedProofs: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      notApproved: [],
      waitingForReview: [],
      paging: {
        "not-approved": { status: 4, target: "notApproved", page: 1, loading: false, exhausted: false },
        waiting: { status: 1, target: "waitingForReview", page: 1, loading: false, exhausted: false },
      },
      loaded: false,
    };
  },
  computed: {
    lists() {
      return [
        {
          key: "not-approved",
          title: "Proofs Not Approved",
          badge: "Not Approved",
          proofs: this.notApproved,
          statusColor: "#EB4654",
          empty: {
            icon: "celebrate",
            title: "All clear — nothing to fix!",
            text: "Not a single proof has been turned down. Your work is landing exactly as it should.",
          },
        },
        {
          key: "waiting",
          title: "Proofs Waiting for Review",
          badge: "Waiting for Review",
          proofs: this.waitingForReview,
          statusColor: "#D9B116",
          empty: {
            icon: "rocket",
            title: "You're all caught up",
            text: "Nothing is waiting on reviewers right now. Send your next proof and keep the momentum going.",
          },
        },
      ];
    },
  },
  async mounted() {
    await Promise.all([this.loadMore("not-approved"), this.loadMore("waiting")]);
    this.loaded = true;
  },
  methods: {
    async loadMore(key) {
      const state = this.paging[key];
      if (state.loading || state.exhausted) return;
      state.loading = true;
      const fetched = await this.$A.ProofService.GetProofsByFilter(
        { ...BASE_FILTERS, status: state.status },
        state.page
      );
      if (fetched.length < 10) state.exhausted = true;
      state.page++;
      this[state.target].push(...this.filterAssociatedProofs(fetched));
      state.loading = false;
      // The permission filter can trim a page below what's needed to make the
      // container scrollable, which would leave no way to trigger the next page.
      this.$nextTick(() => this.fillIfNotScrollable(key));
    },
    fillIfNotScrollable(key) {
      const ref = this.$refs["rows-" + key];
      const el = Array.isArray(ref) ? ref[0] : ref;
      if (el && el.scrollHeight <= el.clientHeight && !this.paging[key].exhausted) {
        this.loadMore(key);
      }
    },
    onScroll(key, e) {
      const el = e.target;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 80) {
        this.loadMore(key);
      }
    },
    filterAssociatedProofs(proofs = []) {
      if (_.isEmpty(this.associatedProofs)) return proofs;
      return proofs.filter((proof) =>
        this.associatedProofs.some((associatedProof) => associatedProof.id === proof.id)
      );
    },
    viewProof(proof) {
      this.$A.NavigateToView("/proof-details", { proof: proof });
    },
    formatDate(date) {
      return date ? moment.utc(date).local().format("MMM D, YYYY") : "";
    },
    hasDocType(proof, types) {
      return (proof.docTypesUsed || []).some((t) => types.indexOf(t) !== -1);
    },
  },
};
</script>

<template>
  <div class="proof-lists">
    <div class="proof-list-card" v-for="list in lists" :key="list.key">
      <div class="card-head">{{ list.title }}</div>
      <div class="proof-rows" :ref="'rows-' + list.key" @scroll="onScroll(list.key, $event)">
        <div class="proof-row" v-for="proof in list.proofs" :key="proof.id">
          <div class="proof-main">
            <a class="proof-name" href="#" @click.prevent="viewProof(proof)">{{ proof.name }}</a>
            <div class="proof-sub">
              <span class="status-badge">
                <span class="status-dot" :style="{ background: list.statusColor }"></span>
                {{ list.badge }}
              </span>
              <span class="version-text">
                Version {{ proof.currentVersion }}<template v-if="proof.currentVersionCreatedAt"> · Created {{ formatDate(proof.currentVersionCreatedAt) }}</template>
              </span>
            </div>
          </div>
          <div class="proof-avatars">
            <span v-if="hasDocType(proof, ['web_url', 'html'])" class="file-pill" :style="{ background: list.statusColor }" title="Web">
              <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8m7.5-6.923c-.67.204-1.335.82-1.887 1.855A7.97 7.97 0 0 0 5.145 4H7.5zM4.09 4a9.27 9.27 0 0 1 .64-1.539 7 7 0 0 1 .597-.933A7.03 7.03 0 0 0 2.255 4zm-.582 3.5c.03-.877.138-1.718.312-2.5H1.674a7 7 0 0 0-.656 2.5zM4.847 5a12.5 12.5 0 0 0-.338 2.5H7.5V5zM8.5 5v2.5h2.99a12.5 12.5 0 0 0-.337-2.5zM4.51 8.5a12.5 12.5 0 0 0 .337 2.5H7.5V8.5zm3.99 0V11h2.653c.187-.765.306-1.608.338-2.5zM5.145 12c.138.386.295.744.468 1.068.552 1.035 1.218 1.65 1.887 1.855V12zm.182 2.472a7 7 0 0 1-.597-.933A9.3 9.3 0 0 1 4.09 12H2.255a7 7 0 0 0 3.072 2.472M3.82 11a13.7 13.7 0 0 1-.312-2.5h-2.49c.062.89.291 1.733.656 2.5zm6.853 3.472A7 7 0 0 0 13.745 12H11.91a9.3 9.3 0 0 1-.64 1.539 7 7 0 0 1-.597.933M8.5 12v2.923c.67-.204 1.335-.82 1.887-1.855.173-.324.33-.682.468-1.068zm3.68-1h2.146c.365-.767.594-1.61.656-2.5h-2.49a13.7 13.7 0 0 1-.312 2.5m2.802-3.5a7 7 0 0 0-.656-2.5H12.18c.174.782.282 1.623.312 2.5zM11.27 2.461c.247.464.462.98.64 1.539h1.835a7 7 0 0 0-3.072-2.472q.337.435.597.933M10.855 4a8 8 0 0 0-.468-1.068C9.835 1.897 9.17 1.282 8.5 1.077V4z"/></svg>
            </span>
            <span v-if="hasDocType(proof, ['image'])" class="file-pill" :style="{ background: list.statusColor }" title="Image">
              <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M6.002 5.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0"/><path d="M2.002 1a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V3a2 2 0 0 0-2-2zm12 1a1 1 0 0 1 1 1v6.5l-3.777-1.947a.5.5 0 0 0-.577.093l-3.71 3.71-2.66-1.772a.5.5 0 0 0-.63.062L1.002 12V3a1 1 0 0 1 1-1z"/></svg>
            </span>
            <span v-if="hasDocType(proof, ['document'])" class="file-pill" :style="{ background: list.statusColor }" title="Document">
              <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M2 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2z"/></svg>
            </span>
            <span v-if="hasDocType(proof, ['video'])" class="file-pill" :style="{ background: list.statusColor }" title="Video">
              <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M0 1a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H1a1 1 0 0 1-1-1zm4 0v6h8V1zm8 7H4v7h8zM1 1v2h2V1zm2 3H1v2h2zM1 7v2h2V7zm2 3H1v2h2zm-2 3v2h2v-2zM15 1h-2v2h2zm-2 3v2h2V4zm2 3h-2v2h2zm-2 3v2h2v-2zm2 3h-2v2h2z"/></svg>
            </span>
            <span v-if="hasDocType(proof, ['audio'])" class="file-pill" :style="{ background: list.statusColor }" title="Audio">
              <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M6 13c0 1.105-1.12 2-2.5 2S1 14.105 1 13s1.12-2 2.5-2 2.5.895 2.5 2m9-2c0 1.105-1.12 2-2.5 2s-2.5-.895-2.5-2 1.12-2 2.5-2 2.5.895 2.5 2"/><path fill-rule="evenodd" d="M14 11V2h1v9zM6 3v10H5V3z"/><path d="M5 2.905a1 1 0 0 1 .9-.995l8-.8a1 1 0 0 1 1.1.995V3L5 4z"/></svg>
            </span>
          </div>
          <a class="view-link" href="#" @click.prevent="viewProof(proof)">View</a>
        </div>

        <div v-if="loaded && !list.proofs.length" class="list-empty">
          <div class="empty-icon" :class="list.empty.icon === 'celebrate' ? 'empty-icon-green' : 'empty-icon-yellow'">
            <svg v-if="list.empty.icon === 'celebrate'" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
            <svg v-else width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </div>
          <div class="empty-title">{{ list.empty.title }}</div>
          <div class="empty-text">{{ list.empty.text }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.proof-lists {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}
@media (max-width: 991px) {
  .proof-lists {
    grid-template-columns: 1fr;
  }
}
.proof-list-card {
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(8, 11, 13, 0.04), 0 8px 24px -16px rgba(8, 11, 13, 0.1);
  display: flex;
  flex-direction: column;
}
.card-head {
  padding: 14px 20px;
  background: #fbfbfc;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 12px 12px 0 0;
  font-size: 15px;
  font-weight: 600;
  color: #111827;
  flex-shrink: 0;
}
.proof-rows {
  max-height: 380px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}
.proof-row {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 13px 20px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  transition: background-color 120ms ease;
}
.proof-row:hover {
  background: #f9f9fa;
}
.proof-row:last-child {
  border-bottom: 0;
}
@media (max-width: 575px) {
  .proof-row {
    flex-wrap: wrap;
    gap: 10px;
    padding: 12px 16px;
  }
  .proof-main {
    flex-basis: 100%;
  }
  .proof-avatars {
    order: 2;
  }
  .view-link {
    order: 3;
    margin-left: auto;
  }
}
.proof-main {
  flex: 1;
  min-width: 0;
}
.proof-name {
  display: inline-block;
  max-width: 100%;
  font-size: 14px;
  font-weight: 400;
  color: #0f172a;
  text-decoration: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  transition: color 120ms ease;
}
.proof-name:hover {
  color: #019256;
  text-decoration: none;
}
.proof-sub {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  font-size: 12px;
  color: #6b7280;
  margin-top: 3px;
}
.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 10px;
  border-radius: 999px;
  background: #f3f4f6;
  color: #4b5563;
  font-size: 11px;
  font-weight: 600;
}
.status-dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 999px;
  flex-shrink: 0;
}
.version-text {
  font-size: 12px;
}
.proof-avatars {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}
.file-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 999px;
  color: #fff;
  flex-shrink: 0;
  box-shadow: 0 0 0 2px #fff;
}
.file-pill svg {
  width: 12px;
  height: 12px;
}
.view-link {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 600;
  color: #019256;
  background: transparent;
  border: 1px solid rgba(1, 146, 86, 0.25);
  border-radius: 6px;
  text-decoration: none;
  flex-shrink: 0;
  transition: background-color 120ms ease, border-color 120ms ease;
}
.view-link:hover {
  background: rgba(1, 146, 86, 0.06);
  border-color: rgba(1, 146, 86, 0.45);
  color: #017a48;
  text-decoration: none;
}
.list-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 48px 32px;
  min-height: 240px;
}
.empty-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 999px;
  margin-bottom: 14px;
}
.empty-icon-green {
  background: rgba(1, 146, 86, 0.1);
  color: #019256;
}
.empty-icon-yellow {
  background: rgba(217, 177, 22, 0.12);
  color: #b08f0e;
}
.empty-title {
  font-size: 15px;
  font-weight: 700;
  color: #111827;
  letter-spacing: -0.01em;
}
.empty-text {
  margin-top: 5px;
  font-size: 12.5px;
  font-weight: 500;
  color: #6b7280;
  line-height: 1.55;
  max-width: 320px;
}
</style>
